import {
  detectWeldIdFromPaths,
  parseBuckerarrestorWeldingTxtContent,
  type BuckerarrestorParsedFile,
} from './buckerarrestorWeldingTxtParser';

export interface BuckerarrestorLoadedTxtFile {
  relativePath: string;
  fileName: string;
  content: string;
}

export interface BuckerarrestorLoadResult {
  files: BuckerarrestorLoadedTxtFile[];
  detectedWeldId: string;
  errors: string[];
}

async function readPlainTxtFiles(fileList: File[]): Promise<BuckerarrestorLoadedTxtFile[]> {
  const entries: BuckerarrestorLoadedTxtFile[] = [];
  for (const file of fileList) {
    const relativePath =
      (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name;
    if (!file.name.toLowerCase().endsWith('.txt')) continue;
    const content = await file.text();
    entries.push({
      relativePath: relativePath.replace(/\\/g, '/'),
      fileName: file.name,
      content,
    });
  }
  return entries;
}

/**
 * Load Manual / Buckerarrestor .txt files from a single file, multi-file, or folder selection.
 * Zip uploads are not supported.
 */
export async function loadBuckerarrestorTxtFilesFromSelection(fileList: FileList | File[]): Promise<BuckerarrestorLoadResult> {
  const files = Array.from(fileList || []);
  const errors: string[] = [];

  if (files.some((f) => f.name.toLowerCase().endsWith('.zip'))) {
    return {
      files: [],
      detectedWeldId: '',
      errors: ['Zip upload is not supported. Choose .txt file(s) or a folder.'],
    };
  }

  const loaded = await readPlainTxtFiles(files);
  const nonTxt = files.filter((f) => !f.name.toLowerCase().endsWith('.txt'));
  if (nonTxt.length) {
    errors.push(`Skipped ${nonTxt.length} non-.txt file(s)`);
  }

  if (!loaded.length) {
    errors.push('No .txt files found in the selection.');
  }

  return {
    files: loaded,
    detectedWeldId: detectWeldIdFromPaths(loaded.map((f) => f.relativePath)),
    errors,
  };
}

export function parseLoadedBuckerarrestorFiles(
  loaded: BuckerarrestorLoadedTxtFile[],
  onProgress?: (done: number, total: number, fileName: string) => void
): { parsed: BuckerarrestorParsedFile[]; errors: string[] } {
  const parsed: BuckerarrestorParsedFile[] = [];
  const errors: string[] = [];
  const total = loaded.length;

  loaded.forEach((file, index) => {
    onProgress?.(index + 1, total, file.fileName);
    try {
      const result = parseBuckerarrestorWeldingTxtContent(file.content, file.fileName);
      if (!result.passName) {
        errors.push(`${file.fileName}: could not extract pass name from filename`);
        return;
      }
      parsed.push(result);
    } catch (e: any) {
      errors.push(`${file.fileName}: ${e?.message || 'parse failed'}`);
    }
  });

  return { parsed, errors };
}
